import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { api } from "../lib/api";
import { AlertTriangle, Zap } from "lucide-react";

export default function QuotaBanner() {
  const [q, setQ] = useState(null);
  const location = useLocation();

  useEffect(() => {
    api
      .get("/quota")
      .then((r) => setQ(r.data))
      .catch(() => setQ(null));
  }, [location.pathname]);

  if (!q || q.plan === "admin") return null;
  if (q.limit === null && !q.blocked) return null; // unlimited (practice)

  // Blocked
  if (q.blocked) {
    const isQuotaExceeded = q.reason === "quota_exceeded";
    return (
      <div
        data-testid="quota-banner-blocked"
        className="px-4 md:px-8 py-2.5 bg-red-500/10 border-b border-red-500/40 flex items-center gap-3"
      >
        <AlertTriangle size={16} className="text-red-400 shrink-0" />
        <div className="flex-1 text-xs text-red-100">
          {isQuotaExceeded ? (
            <>
              You've used all{" "}
              <span className="font-semibold text-red-300">{q.limit}</span> AI
              analyses included in your <span className="uppercase">{q.plan}</span>{" "}
              plan this month.{" "}
              <Link
                to="/account"
                data-testid="quota-banner-upgrade"
                className="underline text-red-300 hover:text-red-200"
              >
                Upgrade to Practice
              </Link>{" "}
              for unlimited usage.
            </>
          ) : (
            <>
              An active subscription is required to run AI analyses.{" "}
              <Link
                to="/account"
                data-testid="quota-banner-subscribe"
                className="underline text-red-300 hover:text-red-200"
              >
                Choose a plan →
              </Link>
            </>
          )}
        </div>
      </div>
    );
  }

  // Active starter with quota — show usage
  if (q.limit && q.remaining !== null) {
    const pct = (q.used_this_month / q.limit) * 100;
    const warning = pct >= 80;
    return (
      <div
        data-testid="quota-banner-usage"
        className={`px-4 md:px-8 py-2 border-b text-xs flex items-center gap-3 ${
          warning
            ? "bg-amber-500/10 border-amber-500/40 text-amber-100"
            : "bg-[var(--pa-bg-2)] border-[var(--pa-border)] text-[var(--pa-text-muted)]"
        }`}
      >
        <Zap
          size={13}
          className={warning ? "text-amber-400" : "text-[var(--pa-gold)]"}
        />
        <div className="flex-1">
          <span className="text-[var(--pa-text)]">{q.used_this_month}</span>
          <span> / </span>
          <span>{q.limit}</span> AI analyses used this month ·{" "}
          <span className="text-[var(--pa-text)]">{q.remaining}</span> remaining
          {warning && (
            <>
              {" — "}
              <Link
                to="/account"
                data-testid="quota-banner-upgrade-near"
                className="underline text-amber-300 hover:text-amber-200"
              >
                upgrade to Practice
              </Link>
            </>
          )}
        </div>
      </div>
    );
  }

  return null;
}
